import { useCallback } from 'react';
import { type StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { capitalizeFirstLetter } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import {
  fallbackToSavedPaymentMethod,
  getCreditCardIcon,
} from '../../../../utils';
import { useNotifyStripeError } from '../../../useNotifyStripeError';
import { removeEndingIn } from '../../helpers';
import type { PaymentMethodChangeHandler } from './types';

/**
 * Handles changes to the payment method, setting state as needed.
 */
export const usePaymentMethodChangeHandler = (
  params: PaymentMethodChangeHandler,
) => {
  const {
    paymentMethods,
    convertTypeToLabelAndIcon,
    setCreatedPaymentMethod,
    setSelectedPaymentMethod,
    setSelectedPaymentIcon,
    createPaymentMethod,
  } = params;

  const { track } = useTelemetry();
  const trackStripeUsage = useCallback(
    (payload: Record<string, unknown>) => {
      track('checkout.stripe-usage', payload);
    },
    [track],
  );

  const notifyStripeError = useNotifyStripeError(trackStripeUsage);

  return useCallback(
    async (event: StripePaymentElementChangeEvent) => {
      try {
        setCreatedPaymentMethod('');

        const id = event.value.payment_method?.id;
        const savedCard = paymentMethods.find((pm) => pm.id === id);

        // Customer selected a card that is already saved in Stripe.
        if (savedCard?.description && savedCard?.cardType) {
          setSelectedPaymentMethod(removeEndingIn(savedCard.description));
          setSelectedPaymentIcon(getCreditCardIcon(savedCard.cardType));
          return;
        }

        // Customer is adding a new card.
        if (event.value.type === 'card' && event.complete) {
          const paymentMethod = await createPaymentMethod(true);

          if (!paymentMethod?.card) return;

          const { brand, last4 } = paymentMethod.card;

          const label = `${capitalizeFirstLetter(brand)} ${last4}`;

          setSelectedPaymentMethod(label);
          setSelectedPaymentIcon(getCreditCardIcon(brand.toUpperCase()));

          return;
        }

        // Customer selected a wallet payment type.
        if (event.value.type) {
          const { label, icon } = convertTypeToLabelAndIcon(event.value.type);
          setSelectedPaymentMethod(label);
          setSelectedPaymentIcon(icon);
        }
      } catch (error) {
        notifyStripeError((error as Error).message);

        const fallback = fallbackToSavedPaymentMethod(paymentMethods);
        const fallbackId = fallback?.id;
        const fallbackCardType = fallback?.cardType;
        setSelectedPaymentMethod(fallbackId);
        setSelectedPaymentIcon(getCreditCardIcon(fallbackCardType));
      }
    },
    [
      setCreatedPaymentMethod,
      paymentMethods,
      setSelectedPaymentMethod,
      setSelectedPaymentIcon,
      createPaymentMethod,
      convertTypeToLabelAndIcon,
      notifyStripeError,
    ],
  );
};
